import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../reducers';
import { doPostLoginActions } from '../actions/login';
import authenticationApi from '../api/authentication/authenticationApi';
import { oauth2Failure, oauth2Request, oauth2Success } from '../reducers/slices/oauth2';
import { setLocale } from '../reducers/slices/locale';
import { normalizeLocale } from '../messages';

export const useOauth2Principal = () => {
  const principal = useSelector((state: AppState) => state.oauth2.principal);
  const loading = useSelector((state: AppState) => state.oauth2.loading);
  const isAuthenticated = useSelector((state: AppState) => state.oauth2.isAuthenticated);

  return {
    loading,
    principal,
    isAuthenticated,
  };
};

export const useOauth2Client = () => {
  // Read the user's currently-selected UI language so the Keycloak login
  // page is rendered in the same language. The value is intentionally read
  // lazily inside the callback so a language change right before redirect
  // is always picked up.
  const currentLang = useSelector((state: AppState) => state.locale.lang);

  const loginRedirect = useCallback(() => {
    authenticationApi.oauth2.loginRedirect(undefined, { locale: currentLang });
  }, [currentLang]);

  const logout = () => {
    authenticationApi.oauth2.logout();
  };

  const logoutRedirect = () => {
    authenticationApi.oauth2.logoutRedirect();
  };

  const updatePasswordRedirect = () => {
    authenticationApi.oauth2.updatePasswordRedirect();
  };

  return {
    loginRedirect,
    logout,
    logoutRedirect,
    updatePasswordRedirect,
  };
};

export const useOauth2Provider = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    async function authenticate() {
      dispatch(oauth2Request());
      try {
        const principal = await authenticationApi.oauth2.getPrincipal();
        const accessTokens = await authenticationApi.oauth2.exchangeAccessTokens();

        // Apply the language chosen on the Keycloak login page (stored on
        // the user's Keycloak profile and returned via the principal) to
        // the platform UI. Done before `doPostLoginActions` so that any
        // localized side effects kicked off there already see the right
        // locale in Redux. If the backend does not return a usable value
        // we keep whatever locale is already persisted and avoid any
        // fallback to English.
        const resolvedLocale = normalizeLocale(principal.locale);
        if (resolvedLocale) {
          dispatch(setLocale(resolvedLocale));
        }

        dispatch(oauth2Success(principal));
        dispatch(doPostLoginActions({
          authMethod: 'oauth2',
          token: accessTokens.token,
          chatToken: accessTokens.chatToken,
        }));
      } catch {
        dispatch(oauth2Failure('Authentication failed using oauth2 provider'));
      }
    }

    authenticate();
  }, [dispatch]);
};
